// MarketWrapPage.js
// Main page for Daily & Weekly Market Roundups.
// Route: /dashboard/market-wrap
// Supports ?tab=daily|weekly and optional ?date=YYYY-MM-DD for deep-linking
// to a specific wrap (e.g. ?tab=weekly&date=2026-04-25).
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useMarketWraps from '../../hooks/useMarketWraps';
import { getMarketWrapService } from '../../Services/MarketWrapService';
import DailyRoundupCard from './DailyRoundupCard';
import WeeklyWrapCard from './WeeklyWrapCard';
import './MarketWrap.css';

const SHARE_BASE = 'https://rightspade.com';

const MarketWrapPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'weekly' ? 'weekly' : 'daily';
    const dateParam  = searchParams.get('date') || null;   // e.g. '2026-04-25' or null

    // When switching tabs, preserve the date param if one is set
    const setActiveTab = (tab) => {
        const params = { tab };
        if (dateParam) params.date = dateParam;
        setSearchParams(params, { replace: true });
    };

    const [copied, setCopied] = useState(false);

    // ── Latest wraps (used when no date param) ────────────────────────────────
    const {
        latestDaily,
        latestWeekly,
        loading: latestLoading,
        error: latestError,
        refetch,
    } = useMarketWraps('both', 1);

    // ── Specific wrap by date (used when ?date=YYYY-MM-DD is in URL) ──────────
    const [specificWrap,    setSpecificWrap]    = useState(null);
    const [specificLoading, setSpecificLoading] = useState(false);
    const [specificError,   setSpecificError]   = useState(null);

    useEffect(() => {
        if (!dateParam) {
            setSpecificWrap(null);
            setSpecificError(null);
            return;
        }
        const wrapId = `${dateParam}_${activeTab}`;
        setSpecificLoading(true);
        setSpecificError(null);
        getMarketWrapService()
            .getWrapById(wrapId)
            .then((wrap) => {
                setSpecificWrap(wrap);
                setSpecificLoading(false);
            })
            .catch((err) => {
                setSpecificError(err.message || 'Failed to load wrap');
                setSpecificLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateParam, activeTab]);

    // ── Resolved values depending on whether a date is pinned ─────────────────
    const latestWrap  = activeTab === 'daily' ? latestDaily : latestWeekly;
    const displayWrap = dateParam ? specificWrap    : latestWrap;
    const loading     = dateParam ? specificLoading : latestLoading;
    const error       = dateParam ? specificError   : latestError;

    const handleRefresh = () => {
        if (dateParam) {
            // Re-trigger specific fetch by toggling a dummy dep via refetch + re-effect
            setSpecificWrap(null);
            const wrapId = `${dateParam}_${activeTab}`;
            setSpecificLoading(true);
            getMarketWrapService()
                .getWrapById(wrapId)
                .then((wrap) => { setSpecificWrap(wrap); setSpecificLoading(false); })
                .catch((err)  => { setSpecificError(err.message); setSpecificLoading(false); });
        } else {
            refetch();
        }
    };

    const handleShare = () => {
        // Always use the full page URL so WhatsApp / social crawlers
        // hit a real URL and pick up the OG tags from index.html.
        const shareUrl = dateParam
            ? `${SHARE_BASE}/dashboard/market-wrap?tab=${activeTab}&date=${dateParam}`
            : `${SHARE_BASE}/dashboard/market-wrap?tab=${activeTab}`;
        const shareTitle = activeTab === 'daily'
            ? 'RightSpade Daily Roundup'
            : 'RightSpade Weekly Wrap';
        const shareText = activeTab === 'daily'
            ? "Today's Indian equity market analysis — Nifty, Sensex, FII/DII flows & more."
            : "This week's Indian equity market wrap — indices, sectors, global cues & outlook.";

        if (navigator.share) {
            navigator.share({ title: shareTitle, text: shareText, url: shareUrl }).catch(() => {});
        } else {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    return (
        <div className="market-wrap-page">
            {/* Page header */}
            <div className="market-wrap-header">
                <div>
                    <h1 className="market-wrap-title">Right Snippets</h1>
                    <p className="market-wrap-subtitle">
                        Daily &amp; Weekly analysis of Indian equity markets
                    </p>
                </div>
                <div className="wrap-header-actions">
                    <button
                        className="wrap-share-btn"
                        onClick={handleShare}
                        title={`Share ${activeTab === 'daily' ? 'Daily Roundup' : 'Weekly Wrap'}`}
                    >
                        {copied ? '✓ Link copied!' : '↗ Share'}
                    </button>
                    <button
                        className="wrap-refresh-btn"
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        {loading ? 'Loading…' : '↻ Refresh'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="wrap-tab-group">
                <button
                    className={`wrap-tab-btn ${activeTab === 'daily' ? 'active' : ''}`}
                    onClick={() => setActiveTab('daily')}
                >
                    Daily Roundup
                </button>
                <button
                    className={`wrap-tab-btn ${activeTab === 'weekly' ? 'active' : ''}`}
                    onClick={() => setActiveTab('weekly')}
                >
                    Weekly Wrap
                </button>
            </div>

            {/* Content */}
            {loading && (
                <div className="wrap-loading">
                    <div className="spinner-border" role="status" style={{ width: '2rem', height: '2rem', color: '#3871E0' }} />
                    <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading snippets…</p>
                </div>
            )}

            {!loading && error && (
                <div className="wrap-empty">
                    <p style={{ color: '#dc2626' }}>⚠ Error loading snippets: {error}</p>
                    <button className="wrap-refresh-btn" onClick={handleRefresh} style={{ marginTop: '0.75rem' }}>
                        Try Again
                    </button>
                </div>
            )}

            {!loading && !error && !displayWrap && (
                <div className="wrap-empty">
                    {dateParam
                        ? <p>No {activeTab} snippet found for <strong>{dateParam}</strong>.</p>
                        : <p>No {activeTab} snippets available yet.</p>
                    }
                    <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#6b7280' }}>
                        {activeTab === 'daily'
                            ? 'Daily snippets are generated automatically on weekdays at 4:00 PM IST.'
                            : 'Weekly snippets are generated automatically on Fridays at 5:00 PM IST.'}
                    </p>
                </div>
            )}

            {!loading && !error && displayWrap && (
                activeTab === 'daily'
                    ? <DailyRoundupCard key={displayWrap.id} wrap={displayWrap} />
                    : <WeeklyWrapCard key={displayWrap.id} wrap={displayWrap} />
            )}

            {/* Disclaimer */}
            {!loading && displayWrap && (
                <p style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    textAlign: 'center',
                    marginTop: '2rem',
                    lineHeight: 1.6,
                }}>
                    This content is compiled from various publicly available data sources, including market research platforms, financial news providers, and analytical tools, and is intended for informational purposes only. It does not constitute investment advice.
                    <br />
                    <br />
                    Right Spade Capital Advisors Pvt. Ltd. is a SEBI Registered Research Analyst (INH000018799). Please consult a qualified financial advisor before making any investment decisions.
                    <br />
                    <br />
                    Kindly refer to our <Link to="/legal#news-policy">News &amp; Research Disclaimer Policy</Link> for details on limitations, and usage guidelines.
                </p>
            )}
        </div>
    );
};

export default MarketWrapPage;
